import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, ListChecks } from 'lucide-react'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Drawer } from '~/components/ui/drawer'
import { cn } from '~/lib/utils'
import { checklistCriteriaSections } from '~/lib/checklist-criteria'
import type { ChecklistItem, CurrentField, SurveyAnswer } from '~/api/types'

export interface SurveyProgressPanelProps {
    /** 응답 완료한 정보(필드) 수. */
    answered: number
    /** 전체 정보(필드) 수. */
    total: number
    /** 현재 묻고 있는 문항. */
    currentItem?: ChecklistItem
    /** 현재 묻고 있는 필드(강조 대상). */
    currentField?: CurrentField
    /** 전체 체크리스트(문항 목록). */
    checklist?: ChecklistItem[] | null
    /** 현재 답안(필드별). */
    answers: SurveyAnswer[]
    /** 설문 완료 여부. */
    done?: boolean
    /** 전체 문항에서 항목을 선택했을 때 호출한다. */
    onSelectItem?(itemId: string): void
}

/** 필드값을 사람이 읽기 좋게 표시한다(TRUE→예 등). */
function displayValue(value: string | undefined, unknown: boolean): string {
    if (unknown) return '모름/해당없음'
    const v = (value ?? '').trim()
    if (!v) return '-'
    const up = v.toUpperCase()
    if (up === 'TRUE') return '예'
    if (up === 'FALSE') return '아니오'
    return v
}

/** 한 문항의 답변 요약 + 응답 완료 여부. */
function itemAnswer(
    item: ChecklistItem,
    byField: Map<string, SurveyAnswer>
): { answered: boolean; summary: string | null } {
    if (item.fields.length > 0) {
        const parts: string[] = []
        let count = 0
        for (const f of item.fields) {
            const a = byField.get(f.fieldId)
            if (a) {
                count += 1
                parts.push(`${f.nameKo}: ${displayValue(a.value, a.unknown)}`)
            }
        }
        return {
            answered: count === item.fields.length,
            summary: parts.length ? parts.join(', ') : null,
        }
    }
    const a = byField.get(`item:${item.id}`)
    return {
        answered: !!a,
        summary: a ? displayValue(a.value, a.unknown) : null,
    }
}

function itemCategoryPath(item: ChecklistItem): string {
    return [item.part, item.majorCategory, item.middleCategory]
        .filter(Boolean)
        .join(' · ')
}

function MetadataRow({
    label,
    value,
}: {
    label: string
    value?: string | number | null
}) {
    return (
        <div className='grid grid-cols-[52px_minmax(0,1fr)] gap-2'>
            <dt className='text-muted-foreground'>{label}</dt>
            <dd className='text-foreground min-w-0 font-medium'>
                {value || '-'}
            </dd>
        </div>
    )
}

/** 우측 패널: 진행 막대 + 현재 문항 카드 + 전체 문항·답변 목록. */
export function SurveyProgressPanel({
    answered,
    total,
    currentItem,
    currentField,
    checklist,
    answers,
    done,
    onSelectItem,
}: SurveyProgressPanelProps) {
    const percent = total > 0 ? Math.round((answered / total) * 100) : 0
    const byField = useMemo(() => {
        const map = new Map<string, SurveyAnswer>()
        for (const a of answers) map.set(a.fieldId, a)
        return map
    }, [answers])
    const items = checklist ?? []
    const currentGuidance = currentItem
        ? checklistCriteriaSections(currentItem)
        : []

    // 기준 섹션 펼침 상태(라벨 기준). 기본은 닫힘 — 담긴 라벨만 펼쳐진 것으로 본다.
    // 문항이 바뀌면 초기화해 새 문항은 다시 닫힌 상태로 보여준다(헤더만 노출, 필요 시 펼침).
    const [openSections, setOpenSections] = useState<Set<string>>(new Set())
    useEffect(() => {
        setOpenSections(new Set())
    }, [currentItem?.id])
    const toggleSection = (key: string) =>
        setOpenSections((prev) => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })

    // "전체 문항 보기" 드로어 + 현재 문항으로 자동 스크롤.
    const [listOpen, setListOpen] = useState(false)
    const currentEntryRef = useRef<HTMLButtonElement>(null)
    useEffect(() => {
        if (listOpen)
            currentEntryRef.current?.scrollIntoView({ block: 'center' })
    }, [listOpen])

    return (
        <div className='flex h-full min-h-0 min-w-0 flex-col gap-4 overflow-hidden'>
            <div>
                <div className='mb-2 flex items-center justify-between gap-2'>
                    <h2 className='text-foreground text-sm font-semibold'>
                        설문 진행
                    </h2>
                    <span
                        className='text-muted-foreground text-xs'
                        aria-live='polite'>
                        {answered}/{total} 정보
                        {total > 0 ? ` (${percent}%)` : ''}
                    </span>
                </div>
                <div
                    className='bg-muted h-2 w-full overflow-hidden rounded-full'
                    role='progressbar'
                    aria-valuemin={0}
                    aria-valuemax={total}
                    aria-valuenow={answered}
                    aria-label='설문 진행도'>
                    <div
                        className='bg-primary h-full transition-all'
                        style={{ width: `${percent}%` }}
                    />
                </div>
            </div>

            {currentItem ? (
                <section
                    aria-label='현재 문항'
                    className='border-primary bg-accent min-h-0 flex-1 overflow-y-auto rounded-lg border p-4'>
                    <div className='mb-2 flex flex-wrap items-center gap-1.5'>
                        <Badge tone='accent'>현재 문항</Badge>
                        <Badge tone='neutral'>
                            {currentItem.classification}
                        </Badge>
                        <span className='text-muted-foreground text-xs'>
                            배점 {currentItem.maxScore}점
                        </span>
                    </div>
                    <p className='text-foreground text-sm font-semibold'>
                        {currentItem.itemNumber ? (
                            <span className='text-muted-foreground'>
                                {currentItem.itemNumber}.{' '}
                            </span>
                        ) : null}
                        {currentItem.title}
                    </p>
                    <dl className='border-border bg-background/70 mt-3 grid gap-1.5 rounded-md border p-3 text-xs'>
                        <MetadataRow label='PART' value={currentItem.part} />
                        <MetadataRow
                            label='대분류'
                            value={currentItem.majorCategory}
                        />
                        <MetadataRow
                            label='중분류'
                            value={currentItem.middleCategory}
                        />
                    </dl>
                    {currentGuidance.length > 0 ? (
                        <div className='mt-3 grid gap-2'>
                            <p className='text-muted-foreground text-xs font-medium'>
                                평가 기준 정보
                            </p>
                            {currentGuidance.map((section, idx) => {
                                const key = `${idx}-${section.title}`
                                const open = openSections.has(key)
                                return (
                                    <div
                                        key={key}
                                        className='border-border bg-card overflow-hidden rounded-md border'>
                                        <button
                                            type='button'
                                            aria-expanded={open}
                                            onClick={() => toggleSection(key)}
                                            className='hover:bg-accent/50 flex w-full items-center justify-between gap-2 px-3 py-2 text-left transition-colors'>
                                            <span className='text-foreground text-xs font-bold'>
                                                {section.title}
                                            </span>
                                            <ChevronDown
                                                aria-hidden
                                                className={cn(
                                                    'text-muted-foreground size-4 shrink-0 transition-transform',
                                                    open ? '' : '-rotate-90'
                                                )}
                                            />
                                        </button>
                                        {open ? (
                                            <p className='text-muted-foreground px-3 pb-3 text-xs leading-relaxed whitespace-pre-line'>
                                                {section.content}
                                            </p>
                                        ) : null}
                                    </div>
                                )
                            })}
                        </div>
                    ) : null}
                    {currentItem.fields.length > 0 ? (
                        <div className='mt-3'>
                            <div className='mb-1 flex items-center justify-between gap-2'>
                                <p className='text-muted-foreground text-xs font-medium'>
                                    필요한 정보
                                </p>
                                {currentField && currentField.of > 0 ? (
                                    <span className='text-muted-foreground text-xs'>
                                        지금 묻는 정보 {currentField.index + 1}/
                                        {currentField.of}
                                    </span>
                                ) : null}
                            </div>
                            <ul className='space-y-1'>
                                {currentItem.fields.map((f) => {
                                    const isAsking =
                                        currentField?.fieldId === f.fieldId
                                    const isAnswered = byField.has(f.fieldId)
                                    return (
                                        <li
                                            key={f.fieldId}
                                            aria-current={
                                                isAsking ? 'step' : undefined
                                            }
                                            className={cn(
                                                'flex items-center gap-1.5 rounded px-1.5 py-0.5 text-sm',
                                                isAsking
                                                    ? 'bg-primary/10 text-foreground font-medium'
                                                    : 'text-foreground'
                                            )}>
                                            <span
                                                aria-hidden
                                                className={cn(
                                                    isAsking
                                                        ? 'text-primary'
                                                        : isAnswered
                                                          ? 'text-success'
                                                          : 'text-muted-foreground'
                                                )}>
                                                {isAsking
                                                    ? '▶'
                                                    : isAnswered
                                                      ? '✓'
                                                      : '•'}
                                            </span>
                                            <span>
                                                {f.nameKo}
                                                {f.unit ? (
                                                    <span className='text-muted-foreground'>
                                                        {' '}
                                                        ({f.unit})
                                                    </span>
                                                ) : null}
                                            </span>
                                            {isAsking ? (
                                                <span className='sr-only'>
                                                    (지금 묻는 정보)
                                                </span>
                                            ) : null}
                                        </li>
                                    )
                                })}
                            </ul>
                        </div>
                    ) : null}
                </section>
            ) : done ? (
                <section
                    aria-label='설문 완료'
                    className='border-border bg-success-subtle min-h-0 flex-1 overflow-y-auto rounded-lg border p-4'>
                    <p className='text-success text-sm font-medium'>
                        모든 문항을 마쳤어요.
                    </p>
                    <p className='text-muted-foreground mt-1 text-xs'>
                        왼쪽에서 채점하기를 눌러 결과를 확인하세요.
                    </p>
                </section>
            ) : null}

            {/* 전체 문항 보기 — 클릭하면 우측 드로어로 전체 리스트를 연다. */}
            {items.length > 0 ? (
                <Button
                    variant='secondary'
                    size='sm'
                    className='w-full shrink-0 justify-between'
                    onClick={() => setListOpen(true)}>
                    <span className='flex items-center gap-1.5'>
                        <ListChecks className='size-4' />
                        전체 문항 보기
                    </span>
                    <Badge tone='neutral'>{items.length}</Badge>
                </Button>
            ) : null}

            <Drawer
                open={listOpen}
                onClose={() => setListOpen(false)}
                title={`전체 문항 (${items.length})`}>
                <ol className='space-y-1.5'>
                    {items.map((item) => {
                        const isCurrent = currentItem?.id === item.id
                        const { answered: itemAnswered, summary } = itemAnswer(
                            item,
                            byField
                        )
                        return (
                            <li
                                key={item.id}
                                aria-current={isCurrent ? 'step' : undefined}>
                                <button
                                    type='button'
                                    ref={
                                        isCurrent ? currentEntryRef : undefined
                                    }
                                    onClick={() => {
                                        onSelectItem?.(item.id)
                                        setListOpen(false)
                                    }}
                                    className={cn(
                                        'w-full rounded-md border px-3 py-2.5 text-left text-xs transition-colors',
                                        isCurrent
                                            ? 'border-primary bg-accent'
                                            : 'border-border bg-card hover:border-primary hover:bg-accent/50'
                                    )}>
                                    <div className='flex items-start justify-between gap-2'>
                                        <span className='text-foreground min-w-0 font-medium'>
                                            {item.itemNumber
                                                ? `${item.itemNumber}. `
                                                : ''}
                                            {item.title}
                                        </span>
                                        <Badge
                                            tone={
                                                isCurrent
                                                    ? 'accent'
                                                    : itemAnswered
                                                      ? 'success'
                                                      : 'neutral'
                                            }>
                                            {isCurrent
                                                ? '현재'
                                                : itemAnswered
                                                  ? '답변완료'
                                                  : '미답'}
                                        </Badge>
                                    </div>
                                    {summary ? (
                                        <p className='text-muted-foreground mt-1'>
                                            <span className='sr-only'>
                                                답변:{' '}
                                            </span>
                                            {summary}
                                        </p>
                                    ) : null}
                                    {itemCategoryPath(item) ? (
                                        <p className='text-muted-foreground mt-1 line-clamp-1'>
                                            {itemCategoryPath(item)}
                                        </p>
                                    ) : null}
                                </button>
                            </li>
                        )
                    })}
                </ol>
            </Drawer>
        </div>
    )
}
